import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import Navbar from '../components/Navbar';
import { toast } from 'sonner';
import {
  Zap, Crown, ArrowLeft, Loader2, RefreshCw, Shield, Timer, Target, Flame, Search,
  TrendingUp, ChevronDown, ChevronUp, BarChart3,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';

const BacktestPanel = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    if (data || loading) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/power-number/backtest`, {
        headers: { Authorization: `Bearer ${token}` }, timeout: 120000,
      });
      setData(res.data);
    } catch (e) {
      toast.error('Error cargando backtest');
    }
    setLoading(false);
  }, [token, data, loading]);

  const toggle = () => {
    setOpen(!open);
    if (!data) load();
  };

  const g = data?.global;
  const baseline = data?.random_baseline;
  const lotteries = data?.lotteries || [];
  const displayed = showAll ? lotteries : lotteries.slice(0, 8);

  const lift = (rate, base) => base > 0 ? (rate / base).toFixed(1) : '0';

  return (
    <div className="mb-6 rounded-xl bg-slate-900/60 border border-emerald-800/30 overflow-hidden"
      data-testid="backtest-panel">
      <button onClick={toggle}
        className="w-full p-4 flex items-center justify-between hover:bg-slate-900/80 transition-colors"
        data-testid="backtest-toggle">
        <div className="flex items-center gap-3">
          <BarChart3 className="text-emerald-400" size={20} />
          <div className="text-left">
            <h3 className="text-white font-bold text-sm">Rendimiento Historico</h3>
            <p className="text-slate-500 text-xs">Aciertos reales del Top 5 en los ultimos sorteos</p>
          </div>
        </div>
        {open ? <ChevronUp className="text-slate-500" size={18} /> : <ChevronDown className="text-slate-500" size={18} />}
      </button>

      {open && (
        <div className="p-4 border-t border-slate-800/50 space-y-4" style={{ animation: 'fadeIn 0.3s ease-out' }}>
          {loading && (
            <div className="flex items-center justify-center py-8 gap-2">
              <Loader2 className="animate-spin text-emerald-400" size={20} />
              <span className="text-slate-500 text-sm">Calculando backtest (puede tardar)...</span>
            </div>
          )}

          {g && !loading && (
            <>
              {/* Global stats */}
              <div className="grid grid-cols-4 gap-2">
                {[
                  { label: 'TOP 1 (#1)', rate: g.top1_rate, base: baseline?.top1_rate, color: 'amber' },
                  { label: 'TOP 3', rate: g.top3_rate, base: 3, color: 'orange' },
                  { label: 'TOP 5', rate: g.top5_rate, base: baseline?.top5_rate, color: 'emerald' },
                  { label: 'Terminal', rate: g.terminal_rate, base: baseline?.terminal_rate, color: 'cyan' },
                ].map((s) => {
                  const x = parseFloat(lift(s.rate, s.base));
                  const good = x >= 1.2;
                  return (
                    <div key={s.label} className={`p-2 rounded-lg bg-slate-800/40 border ${
                      good ? 'border-emerald-700/30' : 'border-slate-700/30'
                    }`}>
                      <div className="text-slate-500 text-[10px] mb-0.5">{s.label}</div>
                      <div className={`text-white font-black text-lg`}>{s.rate}%</div>
                      <div className="flex items-center gap-1 text-[9px] mt-0.5">
                        <span className="text-slate-600">vs {s.base}%</span>
                        <span className={good ? 'text-emerald-400 font-bold' : 'text-slate-500'}>
                          {x}x
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="text-[10px] text-slate-500 flex items-center gap-2">
                <TrendingUp size={11} className="text-emerald-500" />
                <span>
                  <strong className="text-slate-400">{g.draws_analyzed}</strong> sorteos analizados en{' '}
                  <strong className="text-slate-400">{g.lotteries_evaluated}</strong> loterias — ventana {data.backtest_window}
                </span>
              </div>

              {/* Per lottery */}
              <div className="space-y-1">
                <div className="grid grid-cols-12 gap-2 px-2 text-[9px] text-slate-600 font-bold uppercase tracking-wide">
                  <div className="col-span-4">Loteria</div>
                  <div className="col-span-2 text-center">Top 1</div>
                  <div className="col-span-2 text-center">Top 3</div>
                  <div className="col-span-2 text-center">Top 5</div>
                  <div className="col-span-2 text-center">Terminal</div>
                </div>
                {displayed.map((l) => (
                  <div key={l.lottery_id} className="grid grid-cols-12 gap-2 px-2 py-1.5 bg-slate-800/20 rounded-lg items-center text-xs"
                    data-testid={`backtest-row-${l.lottery_id}`}>
                    <div className="col-span-4 flex items-center gap-1.5 min-w-0">
                      <div className="w-1 h-4 rounded-full shrink-0" style={{ backgroundColor: l.color }} />
                      <span className="text-slate-300 truncate text-[11px]">{l.lottery_name}</span>
                    </div>
                    {[l.top1_rate, l.top3_rate, l.top5_rate, l.terminal_rate].map((v, i) => {
                      const base = [1, 3, 5, 50][i];
                      const good = v >= base * 1.3;
                      return (
                        <div key={i} className="col-span-2 text-center">
                          <span className={`font-bold text-[11px] ${
                            v === 0 ? 'text-slate-600' : good ? 'text-emerald-400' : 'text-slate-400'
                          }`}>
                            {v}%
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>

              {lotteries.length > 8 && (
                <button onClick={() => setShowAll(!showAll)}
                  className="w-full py-1.5 text-xs text-emerald-400 hover:text-emerald-300 transition-colors"
                  data-testid="backtest-toggle-all">
                  {showAll ? 'Mostrar menos' : `Ver todas (${lotteries.length - 8} mas)`}
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const CountdownSmall = ({ drawTime }) => {
  const [remaining, setRemaining] = useState('');
  useEffect(() => {
    const update = () => {
      if (!drawTime) return;
      const now = new Date();
      const [h, m] = drawTime.split(':').map(Number);
      const target = new Date(now);
      target.setHours(h, m, 0, 0);
      if (target <= now) target.setDate(target.getDate() + 1);
      const diff = target - now;
      const hrs = Math.floor(diff / 3600000);
      const mins = Math.floor((diff % 3600000) / 60000);
      setRemaining(`${hrs}h ${mins}m`);
    };
    update();
    const iv = setInterval(update, 60000);
    return () => clearInterval(iv);
  }, [drawTime]);
  return <span className="text-slate-600 text-[10px] flex items-center gap-0.5"><Timer size={9} />{remaining}</span>;
};

const PowerCard = ({ prediction }) => {
  const [expanded, setExpanded] = useState(false);
  const pn = prediction.power_numbers || [];
  const main = pn[0];
  const conf = prediction.confidence;
  const confColor = conf >= 80 ? 'text-emerald-400' : conf >= 65 ? 'text-amber-400' : 'text-slate-400';

  return (
    <div className="group rounded-xl bg-slate-900/70 border border-slate-800/50 hover:border-amber-800/40 transition-all overflow-hidden"
      data-testid={`power-${prediction.lottery_id}`}>
      <button className="w-full p-4 flex items-center gap-4 text-left" onClick={() => setExpanded(!expanded)}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <div className="w-2 h-6 rounded-full flex-shrink-0" style={{ backgroundColor: prediction.color || '#22c55e' }} />
            <h3 className="text-white font-bold text-sm truncate">{prediction.lottery_name}</h3>
          </div>
          <div className="flex items-center gap-2 ml-4">
            <span className="text-slate-500 text-xs">{prediction.draw_time}</span>
            {prediction.draw_time && <CountdownSmall drawTime={prediction.draw_time} />}
            {prediction.hot_terminals && prediction.hot_terminals.length > 0 && (
              <span className="text-slate-600 text-[10px] flex items-center gap-1">
                <Flame size={10} className="text-orange-400" />
                <span className="text-orange-300 font-bold">
                  {prediction.hot_terminals.map(t => `x${t}`).join(' · ')}
                </span>
              </span>
            )}
          </div>
        </div>

        {/* Top 5 Power Numbers */}
        <div className="flex items-center gap-1.5">
          {pn.map((p, i) => (
            <div key={`pn-${p.number}`} className={`flex flex-col items-center ${i === 0 ? '' : 'opacity-60'}`}>
              <div className={`rounded-xl flex items-center justify-center shadow-lg transition-transform ${
                i === 0
                  ? 'w-14 h-14 bg-gradient-to-br from-amber-400 via-orange-500 to-red-500 shadow-orange-500/30 group-hover:scale-105'
                  : 'w-9 h-9 bg-slate-800 border border-slate-700'
              }`}>
                <span className={`font-black ${i === 0 ? 'text-white text-xl' : 'text-slate-300 text-xs'}`}>
                  {String(p.number).padStart(2, '0')}
                </span>
              </div>
              {i === 0 && <span className={`text-xs font-bold mt-0.5 ${confColor}`}>{conf}%</span>}
              {p.pools >= 2 && i > 0 && (
                <span className="text-[8px] text-emerald-500 mt-0.5">{p.pools}p</span>
              )}
            </div>
          ))}
        </div>
      </button>

      {expanded && main && (
        <div className="px-4 pb-4 border-t border-slate-800/50 space-y-3" style={{ animation: 'fadeIn 0.3s ease-out' }}>
          {/* Main number detail */}
          <div className="mt-3 flex items-center gap-3">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 via-orange-500 to-red-500 flex items-center justify-center shadow-lg shadow-orange-500/30">
              <span className="text-white font-black text-2xl">{String(main.number).padStart(2, '0')}</span>
            </div>
            <div className="flex-1">
              <p className="text-white font-bold text-sm">Numero Poderoso #1</p>
              <p className="text-slate-400 text-xs mt-0.5">{main.reason}</p>
              <div className="flex gap-1 mt-1">
                {main.in_same_terminal && <Badge variant="outline" className="text-[9px] py-0 border-orange-500/30 text-orange-300">Terminal</Badge>}
                {main.in_neighbor && <Badge variant="outline" className="text-[9px] py-0 border-cyan-500/30 text-cyan-300">Vecino</Badge>}
                {main.in_frequent && <Badge variant="outline" className="text-[9px] py-0 border-purple-500/30 text-purple-300">Frecuente</Badge>}
                {main.pools >= 2 && <Badge variant="outline" className="text-[9px] py-0 border-emerald-500/30 text-emerald-300">{main.pools} pools</Badge>}
              </div>
            </div>
          </div>

          {/* Score breakdown */}
          <div className="grid grid-cols-5 gap-1 text-center">
            {main.details && Object.entries(main.details).map(([k, v]) => (
              <div key={k} className="p-1.5 bg-slate-800/40 rounded-lg">
                <div className="h-1 bg-slate-700 rounded-full overflow-hidden mb-1">
                  <div className={`h-full rounded-full ${v >= 50 ? 'bg-emerald-400' : v >= 20 ? 'bg-amber-400' : 'bg-slate-600'}`}
                    style={{ width: `${Math.min(v, 100)}%` }} />
                </div>
                <span className="text-[8px] text-slate-500 block">{k}</span>
                <span className="text-white text-[10px] font-bold">{Math.round(v)}</span>
              </div>
            ))}
          </div>

          {/* All 5 power numbers */}
          <div>
            <span className="text-slate-500 text-[10px] block mb-1">Top 5 Poderosos para Posicion 1:</span>
            <div className="space-y-1">
              {pn.map((p) => (
                <div key={`det-${p.number}`} className="flex items-center gap-2 p-1.5 bg-slate-800/20 rounded-lg">
                  <span className={`w-7 h-7 rounded-lg flex items-center justify-center font-black text-xs ${
                    p.rank === 1 ? 'bg-amber-500/20 text-amber-400' : 'bg-slate-800 text-slate-400 border border-slate-700'
                  }`}>
                    {String(p.number).padStart(2, '0')}
                  </span>
                  <div className="flex-1">
                    <span className="text-slate-400 text-[10px]">{p.reason}</span>
                  </div>
                  <div className="flex gap-0.5">
                    {p.in_same_terminal && <span className="w-1.5 h-1.5 rounded-full bg-orange-400" />}
                    {p.in_neighbor && <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" />}
                    {p.in_frequent && <span className="w-1.5 h-1.5 rounded-full bg-purple-400" />}
                  </div>
                  <span className="text-white text-[10px] font-bold">{p.score}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Last draw */}
          <div className="flex items-center gap-2 text-[10px] text-slate-500">
            <span>Ultimo sorteo pos1:</span>
            <span className="font-bold text-white">{String(prediction.last_pos1).padStart(2, '0')}</span>
            <span>| Completo: {prediction.last_draw?.map(n => String(n).padStart(2, '0')).join('-')}</span>
          </div>

          {/* Hot Terminals (high-hit signal) */}
          {prediction.hot_terminals && prediction.hot_terminals.length > 0 && (
            <div className="p-2 rounded-lg bg-gradient-to-r from-orange-950/40 to-red-950/30 border border-orange-800/30"
              data-testid={`hot-terminals-${prediction.lottery_id}`}>
              <div className="flex items-center gap-2 mb-1">
                <Flame size={12} className="text-orange-400" />
                <span className="text-orange-300 font-bold text-[11px]">Terminales Calientes (mayor probabilidad)</span>
              </div>
              <div className="flex gap-2">
                {prediction.hot_terminals.map((t, i) => (
                  <div key={`ht-${t}`} className={`flex-1 p-1.5 rounded-lg text-center ${
                    i === 0 ? 'bg-orange-500/20 border border-orange-500/40'
                    : i === 1 ? 'bg-orange-600/15 border border-orange-600/30'
                    : 'bg-orange-700/10 border border-orange-700/25'
                  }`}>
                    <div className="text-orange-300 font-black text-lg">x{t}</div>
                    <div className="text-slate-500 text-[8px]">
                      {i === 0 ? 'caliente #1' : i === 1 ? 'caliente #2' : 'caliente #3'}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-slate-500 text-[9px] mt-1.5">
                Historicamente el numero ganador termina en uno de estos digitos {'>'} 35% del tiempo.
              </p>
            </div>
          )}

          {/* Extended candidates (top 10) */}
          {prediction.extended_candidates && prediction.extended_candidates.length > 5 && (
            <details className="rounded-lg bg-slate-800/20">
              <summary className="cursor-pointer p-2 text-[11px] text-cyan-400 hover:text-cyan-300">
                Ver 5 candidatos adicionales (Top 10)
              </summary>
              <div className="p-2 grid grid-cols-5 gap-1">
                {prediction.extended_candidates.slice(5).map(p => (
                  <div key={`ext-${p.number}`} className="p-1 rounded bg-slate-800/40 text-center">
                    <div className="text-slate-300 font-bold text-sm">{String(p.number).padStart(2, '0')}</div>
                    <div className="text-slate-600 text-[8px]">#{p.rank} · {p.score}</div>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
};

export default function PowerNumberPage() {
  const { user, token } = useAuth();
  const [predictions, setPredictions] = useState([]);
  const [universal, setUniversal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/power-number/today`, {
        headers: { Authorization: `Bearer ${token}` }, timeout: 30000,
      });
      setPredictions(res.data.predictions || []);
      setUniversal(res.data.universal_power || null);
    } catch (e) {
      toast.error('Error cargando Numero Poderoso');
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { if (token) load(); }, [token, load]);

  const filtered = search
    ? predictions.filter(p => p.lottery_name?.toLowerCase().includes(search.toLowerCase()))
    : predictions;

  if (!user) return (
    <div className="min-h-screen bg-[#0a0e1a] flex items-center justify-center">
      <p className="text-slate-400">Inicia sesion</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0a0e1a]" data-testid="power-number-page">
      <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }`}</style>
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 py-6 pt-20">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Link to="/dashboard">
              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white"><ArrowLeft size={16} /></Button>
            </Link>
            <div>
              <h1 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-red-500 flex items-center gap-2">
                <Crown className="text-amber-400" size={24} /> Numero Poderoso V4
              </h1>
              <p className="text-slate-500 text-sm">Senales robustas + Terminales Calientes — Posicion 1</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}
            className="border-amber-700/40 text-amber-400 hover:text-white" data-testid="power-refresh">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>

        {/* Universal Power Number */}
        {universal && (
          <div className="mb-6 p-5 rounded-xl bg-gradient-to-r from-amber-950/40 via-red-950/30 to-orange-950/40 border border-amber-700/40 shadow-lg shadow-amber-900/10"
            style={{ animation: 'fadeIn 0.5s ease-out' }} data-testid="universal-power">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-amber-400 via-orange-500 to-red-600 flex items-center justify-center shadow-xl shadow-orange-500/40">
                <span className="text-white font-black text-3xl">{String(universal.number).padStart(2, '0')}</span>
              </div>
              <div>
                <h2 className="text-amber-400 font-black text-lg">Numero Poderoso Universal</h2>
                <p className="text-slate-400 text-sm">
                  Aparece como fuerte en <strong className="text-white">{universal.appears_in} de {universal.total_lotteries}</strong> loterias ({universal.percentage}%)
                </p>
                <p className="text-slate-500 text-xs mt-1">Score promedio: {universal.avg_score}</p>
              </div>
            </div>
          </div>
        )}

        {/* Historical performance */}
        <BacktestPanel token={token} />

        <div className="mb-4 p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
          <div className="flex items-start gap-2 text-xs text-slate-400">
            <Shield size={14} className="text-amber-400 mt-0.5 shrink-0" />
            <div className="space-y-1">
              <p>
                <strong className="text-amber-300">Poderoso V4</strong> usa cuatro senales validadas por backtest:
                <strong className="text-white"> frecuencia reciente (50%)</strong>,
                <strong className="text-orange-300"> terminales calientes (25%)</strong>,
                <strong className="text-white"> momentum (15%)</strong> y patron del dia (10%).
              </p>
              <p className="text-slate-500 text-[11px]">
                <strong className="text-emerald-400">Realismo:</strong> la loteria es fundamentalmente aleatoria.
                El motor ofrece una ventaja estadistica (~1.6x sobre el azar para Top 5).
                Las <strong className="text-orange-300">Terminales Calientes</strong> son la senal mas confiable (acierto {'>'} 35%).
              </p>
            </div>
          </div>
        </div>

        <div className="mb-4 relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input placeholder="Buscar loteria..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="bg-slate-900/70 border-slate-800 text-white pl-10" data-testid="power-search" />
        </div>

        {loading ? (
          <div className="flex flex-col items-center py-20 gap-3">
            <Loader2 className="animate-spin text-amber-400" size={36} />
            <p className="text-slate-500 text-sm">Calculando numeros poderosos...</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="power-list">
            {filtered.map(p => <PowerCard key={p.lottery_id} prediction={p} />)}
            {filtered.length === 0 && <div className="text-center py-12 text-slate-500">No se encontraron loterias</div>}
          </div>
        )}
      </div>
    </div>
  );
}
